import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Bar,
  BarChart,
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  CartesianGrid,
} from 'recharts';
import { CircleCheck, CircleHelp, CircleX, ShieldCheck, Info } from 'lucide-react';
import { deliveryQualityService } from 'services/deliveryQualityService';
import { Card } from '../../../../components/ui/Card';
import { Badge } from '../../../../components/ui/Badge';
import { Table, THead, TBody, TR, TH, TD } from '../../../../components/ui/Table';
import { useIsMobile } from '../../../../hooks/useIsMobile';
import KpiCard from '../components/KpiCard';
import { KpiRowSkeleton, SkeletonPanel, CardTableSkeleton } from './TabSkeletons';
import { formatPercent as fmtPct } from '../../../../utils/formatters';

// Paletas de recharts (excepción tolerada): verde/ámbar/rojo semánticos.
const VERDICT_COLORS = {
  validated: '#10B981',
  doubtful: '#F59E0B',
  rejected: '#DC2626',
};

const CONFIDENCE_COLORS = {
  high: '#10B981',
  medium: '#F59E0B',
  low: '#DC2626',
};

const SUBCRITERION_LABELS = {
  package_label: 'Etiqueta del paquete',
  address_number: 'Número de domicilio',
  receiver_evidence: 'Receptor / lugar entregado',
};

const AiAnalysisTab = ({ filters }) => {
  const { t } = useTranslation('deliveryquality');
  const isMobile = useIsMobile();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError(null);
    deliveryQualityService
      .getGlobal(filters)
      .then((r) => alive && setData(r.data))
      .catch((e) => alive && setError(e?.message || 'Error'))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [filters]);

  if (loading) {
    // Skeleton acorde al contenido: 4 KPIs + 2 charts + tabla.
    return (
      <>
        <KpiRowSkeleton count={4} />
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 mb-3">
          <SkeletonPanel height={372} />
          <SkeletonPanel height={372} />
        </div>
        <CardTableSkeleton rows={6} columns={5} />
      </>
    );
  }
  if (error) {
    return (
      <div className="rounded-lg border border-status-error-soft bg-status-error-soft text-status-error px-4 py-3 text-sm">
        {t('No se pudo cargar el análisis IA')}: {error}
      </div>
    );
  }
  if (!data || !data.total_evaluations) {
    return (
      <div className="rounded-lg border border-border bg-bg-muted text-fg-muted px-4 py-3 text-sm">
        {t('Sin evaluaciones para el rango seleccionado.')}
      </div>
    );
  }

  const ai = data.ai_breakdown || {};
  const withAi = ai.with_ai_count || 0;
  const verdictDist = ai.verdict_distribution || {};
  const verdictRates = ai.verdict_rates || {};
  const confDist = ai.confidence_distribution || {};
  const confRates = ai.confidence_rates || {};
  const bySub = ai.by_subcriterion || {};

  if (!withAi) {
    return (
      <div className="rounded-lg border border-status-warning-soft bg-status-warning-soft text-status-warning px-4 py-3 text-sm">
        {t('Aún no hay paquetes con análisis IA respondido en el rango seleccionado.')}
      </div>
    );
  }

  const verdictBars = [
    { name: t('Validadas'), value: verdictDist.validated || 0, key: 'validated' },
    { name: t('Dudosas'), value: verdictDist.doubtful || 0, key: 'doubtful' },
    { name: t('Rechazadas'), value: verdictDist.rejected || 0, key: 'rejected' },
  ];

  const confidencePie = [
    { name: t('Alta'), value: confDist.high || 0, key: 'high' },
    { name: t('Media'), value: confDist.medium || 0, key: 'medium' },
    { name: t('Baja'), value: confDist.low || 0, key: 'low' },
  ];

  const subRows = Object.entries(SUBCRITERION_LABELS).map(([key, label]) => {
    const c = bySub[key] || {};
    return { key, label, avg: c.avg ?? 0, passRate: c.pass_rate ?? 0, weight: c.weight ?? 0 };
  });

  const aiCriterion = data.by_criterion?.images_ai || {};

  return (
    <>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-3">
        <KpiCard
          title={t('% Validadas')}
          value={fmtPct(verdictRates.validated)}
          sub={t('{{n}} de {{total}} con IA', { n: verdictDist.validated || 0, total: withAi })}
          tone="success"
          icon={CircleCheck}
        />
        <KpiCard
          title={t('% Dudosas')}
          value={fmtPct(verdictRates.doubtful)}
          sub={t('{{n}} entregas', { n: verdictDist.doubtful || 0 })}
          tone="warning"
          icon={CircleHelp}
        />
        <KpiCard
          title={t('% Rechazadas')}
          value={fmtPct(verdictRates.rejected)}
          sub={t('{{n}} entregas', { n: verdictDist.rejected || 0 })}
          tone="error"
          icon={CircleX}
        />
        <KpiCard
          title={t('Confianza ALTA')}
          value={fmtPct(confRates.high)}
          sub={t('{{n}} resoluciones', { n: confDist.high || 0 })}
          tone="success"
          icon={ShieldCheck}
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 mb-3 items-stretch">
        <Card padding="none" className="h-full overflow-hidden">
          <div className="px-4 py-3 border-b border-border bg-bg-muted text-sm font-semibold text-fg">
            {t('Distribución de veredicto IA')}
          </div>
          <div className="p-4" style={{ height: 320 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={verdictBars}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="value" name={t('Paquetes')}>
                  {verdictBars.map((b) => (
                    <Cell key={b.key} fill={VERDICT_COLORS[b.key]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>
        <Card padding="none" className="h-full overflow-hidden">
          <div className="px-4 py-3 border-b border-border bg-bg-muted text-sm font-semibold text-fg">
            {t('Distribución de confianza IA')}
          </div>
          <div className="p-4" style={{ height: 320 }}>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={confidencePie}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  outerRadius={100}
                  label
                >
                  {confidencePie.map((b) => (
                    <Cell key={b.key} fill={CONFIDENCE_COLORS[b.key]} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </Card>
      </div>

      <Card padding="none" className="overflow-hidden">
        <div className="px-4 py-3 border-b border-border bg-bg-muted flex flex-col sm:flex-row sm:justify-between sm:items-center gap-1">
          <span className="text-sm font-semibold text-fg">{t('Sub-criterios del análisis IA')}</span>
          <small className="text-fg-subtle text-xs">
            {t('Score IA combinado')}: {(aiCriterion.avg ?? 0).toFixed(1)} —{' '}
            {fmtPct(aiCriterion.pass_rate)} {t('validadas')}
          </small>
        </div>
        <div className="p-3">
          {isMobile ? (
            <div className="flex flex-col gap-2">
              {subRows.map((r) => (
                <div key={r.key} className="rounded-lg border border-border p-3 flex flex-col gap-1.5">
                  <span className="text-sm font-medium text-fg">{t(r.label)}</span>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-fg-muted">{t('Promedio')}</span>
                    <span className="font-semibold text-fg tabular-nums">{(r.avg ?? 0).toFixed(1)}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-fg-muted">{t('% válidas')}</span>
                    <span className="font-semibold text-fg tabular-nums">{fmtPct(r.passRate)}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-fg-muted">{t('Peso (% de 35)')}</span>
                    <Badge tone="neutral">{r.weight}%</Badge>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <Table>
              <THead>
                <TR>
                  <TH>{t('Criterio')}</TH>
                  <TH
                    className="text-right"
                    title={t('Promedio del sub-score (0–100). Se aplica el factor por nivel de confianza global del result (ALTO=1.0, MEDIO=0.85, BAJO=0.70).')}
                  >
                    <span className="inline-flex items-center gap-1 justify-end">
                      {t('Promedio')} <Info className="w-3 h-3 opacity-60" />
                    </span>
                  </TH>
                  <TH className="text-right">{t('% válidas')}</TH>
                  <TH className="text-right">{t('Peso (% de 35)')}</TH>
                </TR>
              </THead>
              <TBody>
                {subRows.map((r) => (
                  <TR key={r.key}>
                    <TD>{t(r.label)}</TD>
                    <TD className="text-right tabular-nums">{(r.avg ?? 0).toFixed(1)}</TD>
                    <TD className="text-right tabular-nums">{fmtPct(r.passRate)}</TD>
                    <TD className="text-right"><Badge tone="neutral">{r.weight}%</Badge></TD>
                  </TR>
                ))}
              </TBody>
            </Table>
          )}
          <div className="text-xs text-fg-subtle mt-2 flex items-center gap-1">
            <Info className="w-3.5 h-3.5" />
            {t('Total con análisis IA respondido')}: <b>{withAi}</b> {t('de')}{' '}
            {data.total_evaluations} {t('entregas en el rango.')}.
          </div>
        </div>
      </Card>
    </>
  );
};

export default AiAnalysisTab;
